import React from 'react';
import type { Language, LanguageCode } from '../../../../shared/types';
import { LoadingSpinner } from '../common/LoadingSpinner.js';

interface LanguageSelectorProps {
  languages: Language[];
  selectedLanguage?: LanguageCode | null;
  onSelect: (languageCode: LanguageCode) => void;
  isLoading?: boolean;
  showDescriptions?: boolean;
  layout?: 'grid' | 'list';
}

export function LanguageSelector({
  languages,
  selectedLanguage,
  onSelect,
  isLoading = false,
  showDescriptions = true,
  layout = 'grid',
}: LanguageSelectorProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (languages.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No languages available at this time.</p>
      </div>
    );
  }

  // Sort languages by display order
  const sortedLanguages = [...languages].sort((a, b) => a.displayOrder - b.displayOrder);

  const containerClass =
    layout === 'grid'
      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
      : 'flex flex-col space-y-3';

  return (
    <div className={containerClass}>
      {sortedLanguages.map((language) => (
        <LanguageCard
          key={language.code}
          language={language}
          isSelected={selectedLanguage === language.code}
          onSelect={() => onSelect(language.code)}
          showDescription={showDescriptions}
        />
      ))}
    </div>
  );
}

interface LanguageCardProps {
  language: Language;
  isSelected: boolean;
  onSelect: () => void;
  showDescription: boolean;
}

function LanguageCard({ language, isSelected, onSelect, showDescription }: LanguageCardProps) {
  const baseClasses =
    'relative rounded-lg border-2 p-6 cursor-pointer transition-all duration-200 hover:shadow-lg';
  const selectedClasses = isSelected
    ? 'border-primary-600 bg-primary-50 shadow-md'
    : 'border-gray-300 bg-white hover:border-primary-300';

  return (
    <button
      onClick={onSelect}
      className={`${baseClasses} ${selectedClasses} text-left w-full`}
      aria-pressed={isSelected}
      disabled={!language.enabled}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-3 right-3">
          <svg
            className="w-6 h-6 text-primary-600"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}

      {/* Language flag and name */}
      <div className="flex items-center space-x-3 mb-2">
        <span className="text-4xl" role="img" aria-label={`${language.name} flag`}>
          {language.flag}
        </span>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{language.name}</h3>
          <p className="text-sm text-gray-600">{language.nativeName}</p>
        </div>
      </div>

      {/* Description */}
      {showDescription && (
        <div className="mt-3 space-y-1">
          <div className="flex items-center text-sm text-gray-600">
            <svg
              className="w-4 h-4 mr-2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <span>
              {language.hasInitialContent && language.estimatedVocabularySize > 0
                ? `~${language.estimatedVocabularySize.toLocaleString()} words`
                : 'Vocabulary coming soon'}
            </span>
          </div>

          <div className="flex items-center text-sm text-gray-600">
            <svg
              className="w-4 h-4 mr-2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{language.proficiencyFramework} levels: {language.proficiencyLevels.join(', ')}</span>
          </div>

          {language.voiceSupported && (
            <div className="flex items-center text-sm text-gray-600">
              <svg
                className="w-4 h-4 mr-2 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
              <span>Voice practice supported</span>
            </div>
          )}

          {!language.enabled && (
            <div className="mt-2 text-sm text-amber-600 font-medium">
              Coming soon
            </div>
          )}
        </div>
      )}
    </button>
  );
}
